import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Feather, Loader2, Languages, Gift } from "lucide-react";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import PasswordInput from "@/components/PasswordInput";
import api from "@/lib/api";

export default function RegisterPage() {
  const { register, error } = useAuth();
  const { t, lang, changeLanguage } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState("");

  useEffect(() => {
    const ref = (params.get("ref") || "").toUpperCase();
    if (ref) localStorage.setItem("aviary_ref", ref);
    const code = ref || localStorage.getItem("aviary_ref");
    if (!code) return;
    api.get(`/referrals/validate/${code}`).then(({ data }) => setReferrer(data.referrer_name)).catch(() => localStorage.removeItem("aviary_ref"));
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await register(email, password, name);
    setLoading(false);
    if (ok) navigate("/couples", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1774815656770-28f98e78fa2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxleG90aWMlMjBiaXJkcyUyMHBhcnJvdCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzc4Njk1NjU0fDA&ixlib=rb-4.1.0&q=85"
          alt="Parrot"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-espresso/30" />
        <div className="absolute bottom-10 left-10 text-white max-w-md">
          <h2 className="font-heading text-4xl font-semibold leading-tight">
            Join your flock. Sync your records across every device.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute top-4 right-4 rtl:right-auto rtl:left-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => changeLanguage(lang === "ar" ? "en" : "ar")}
            className="border-olive/40 text-olive hover:bg-olive/10 text-xs font-semibold"
            data-testid="register-language-toggle"
          >
            <Languages className="w-3.5 h-3.5 mr-1" />
            {lang === "ar" ? "English" : "العربية"}
          </Button>
        </div>
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6"
          data-testid="register-form"
        >
          <div>
            <div className="flex items-center gap-2 text-olive mb-2">
              <Feather className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-[0.3em]">{t("app.brand")}</span>
            </div>
            <h1 className="font-heading text-4xl font-semibold text-espresso">{t("auth.signup_title")}</h1>
            <p className="text-sm text-espresso/70 mt-2">{t("auth.signup_sub")}</p>
          </div>

          {referrer && (
            <div className="flex items-center gap-2 bg-olive/10 border border-olive/30 text-olive rounded-lg px-3 py-2 text-sm" data-testid="register-referrer-note">
              <Gift className="w-4 h-4 shrink-0" />
              <span>{t("ref.invited_by").replace("{name}", referrer)}</span>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">{t("common.name")}</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("common.name")} data-testid="register-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">{t("common.email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@aviary.com" data-testid="register-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password_min")}</Label>
              <PasswordInput
                id="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                testId="register-password-input"
                testIdSuffix="register-password"
                autoComplete="new-password"
                className="mt-1.5"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2"
              data-testid="register-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading}
            className="w-full bg-olive hover:bg-olive-hover text-white h-11 rounded-lg"
            data-testid="register-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.signup")}
          </Button>

          <SocialAuthButtons />

          <p className="text-sm text-center text-espresso/70">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-olive font-semibold hover:underline" data-testid="login-link">
              {t("common.signin")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
